import { IBooleanResponse } from '@gem/models/commonModel';
import { logger } from '@gem/modules/logger';

import { mapGroupCounterView } from 'APP/model/counter/counterModel.b2b.mapper';
import { buildModelError } from 'APP/model/error/errorBuilder';
import { WsRequestError } from 'APP/model/error/errors/WsRequestError';
import { mapPersonalizedUserResponse } from 'APP/model/user/userModel.b2b.mapper';
import { ServerErrorCode } from 'APP/restApi/constants/constants';
import { GroupServerErrorCode } from 'APP/restApi/constants/group.errors';
import {
  addChatAdminsRequest,
  addChatParticipantsRequest,
  clearChatHistoryForAllRequest,
  clearChatHistoryForMeRequest,
  createChatRequest,
  getChatsByIdsRequest,
  getChatUsersRequest,
  getUsersAddToChatRequest,
  joinChatRequest,
  leaveChatRequest,
  muteChatRequest,
  pinChatRequest,
  pinMessagesRequest,
  removeChatParticipantsRequest,
  removeChatRequest,
  revokeChatAdminsRequest,
  transferOwnershipRequest,
  unpinAllMessagesRequest,
  unpinChatRequest,
  unpinMessagesRequest,
  updateChatRequest,
  setUserTypingMessageRequest,
  markChatsAsUnreadRequest,
  setChatMessagesHistoryTtlRequest,
} from 'APP/wsApi/wsAppApi/requests/chatRequests';
import { refreshDeepLinkDetailsRequest } from 'APP/wsApi/wsAppApi/requests/deeplinkRequests';

import {
  mapChatParticipant,
  mapGroupTypeToChatType,
  mapPersonalizedChatResponse,
  mapSetChatMessagesHistoryTtlRequest,
} from './groupModel.b2b.mapper';
import {
  IBanUsersForGroupRequest,
  IChangeOwnershipRequest,
  IClearGroupMessageRequest,
  ICollectionResponsePersonalizedGroup,
  ICreateGroupRequest,
  IDeepLinkExistsRequest,
  IDeleteGroupDeepLinkRequest,
  IGetBannedUsersForGroupRequest,
  IGetBannedUsersForGroupResponse,
  IGetFriendsNotInvitedToGroupRequest,
  IGetFriendsNotInvitedToGroupResponse,
  IGetGroupUserRolesRequest,
  IGetOrCreateThreadRequest,
  IGetThreadInfoByIdRequest,
  IGetThreadsInfoRequest,
  IGetUsersAddToGroupRequest,
  IGetUsersAddToGroupResponse,
  IGroupAdvertisementSettingsResponse,
  IGroupIdRequest,
  IGroupParticipantChangeRequest,
  IGroupSettings,
  IGroupUserRolesResponse,
  IGroupWithBlockStatusResponse,
  IMemberCustomPermissions,
  IMemberCustomPermissionsRequest,
  IPersonalizedGroup,
  IPinGroupRequest,
  IPinMessagesRequest,
  ISetChatMessagesHistoryTtlRequest,
  ISetGroupAdvertisementSettingsRequest,
  ISetGroupSettings,
  ISetKeywordFiltersRequest,
  ISetObsceneFilterRequest,
  IThreadInfoResponse,
  IThreadsInfoResponse,
  IUnbanUsersForGroupRequest,
  IUpdateGroupInfoRequest,
} from './groupModel.types';

export const pinMessages = async (data: IPinMessagesRequest): Promise<IBooleanResponse> => {
  try {
    const result = await pinMessagesRequest({
      chatId: BigInt(data.groupId),
      messageIds: data.messageIds.map((messageId) => BigInt(messageId)),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.pinMessages', error);
    throw buildModelError(error);
  }
};

export const unpinMessages = async (data: IPinMessagesRequest): Promise<IBooleanResponse> => {
  try {
    const result = await unpinMessagesRequest({
      chatId: BigInt(data.groupId),
      messageIds: data.messageIds.map((messageId) => BigInt(messageId)),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.unpinMessages', error);
    throw buildModelError(error);
  }
};

export const unpinAllMessages = async (data: IGroupIdRequest): Promise<IBooleanResponse> => {
  try {
    const result = await unpinAllMessagesRequest({ chatId: BigInt(data.groupId) });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.unpinAllMessages', error);
    throw buildModelError(error);
  }
};

export const markGroupsAsUnread = async (groupIds: string[], unread = true): Promise<boolean> => {
  try {
    const result = await markChatsAsUnreadRequest({
      chatIds: groupIds.map((item) => BigInt(item)),
      unread: unread,
    });

    if (result.type.case === 'success') {
      return result.type.value.success;
    } else {
      return false;
    }
  } catch (error) {
    logger.get('API').error('group.markGroupsAsUnread', error);
    throw buildModelError(error);
  }
};

// No need to BFF implement, this functionality has been closed by the feature flags
export const setMemberPermissions = async (
  _data: IMemberCustomPermissionsRequest
): Promise<IMemberCustomPermissions> => {
  try {
    return {} as IMemberCustomPermissions;
  } catch (error) {
    logger.get('API').error('setMemberPermissions', error);
    throw buildModelError(error);
  }
};

// No need to BFF implement, this functionality has been closed by the feature flags
export const setGroupSettings = async (_data: ISetGroupSettings): Promise<IGroupSettings> => {
  try {
    return { systemMessagesEnabled: false };
  } catch (error) {
    logger.get('API').error('setGroupSettings', error);
    throw buildModelError(error);
  }
};

// No need to BFF implement, this functionality has been closed by the feature flags
export const setObsceneFilter = async (_data: ISetObsceneFilterRequest): Promise<boolean> => {
  try {
    return false;
  } catch (error) {
    logger.get('API').error('group.setObsceneFilter', error);
    throw buildModelError(error);
  }
};

// No need to BFF implement, this functionality has been closed by the feature flags
export const showContent = async (_groupId: string): Promise<void> => {
  try {
    return;
  } catch (error) {
    logger.get('API').error('group.showContent', error);
    throw buildModelError(error);
  }
};

// No need to BFF implement, this functionality has been closed by the feature flags
export const banUsersForGroup = async (
  _data: IBanUsersForGroupRequest
): Promise<IBooleanResponse> => {
  try {
    return { result: false };
  } catch (error) {
    logger.get('API').error('group.banUsersForGroup', error);
    throw buildModelError(error);
  }
};

export const getGroupUserRoles = async (
  data: IGetGroupUserRolesRequest
): Promise<IGroupUserRolesResponse> => {
  try {
    const result = await getChatUsersRequest({
      ...data,
      chatId: BigInt(data.groupId),
    });

    return {
      participants: (result.type.value?.users || []).map((user) => mapChatParticipant(user)),
      cursor: result.type.value?.cursor,
    };
  } catch (error) {
    logger.get('API').error('group.getGroupUserRoles', error);
    throw buildModelError(error);
  }
};

export const transferOwnership = async (
  data: IChangeOwnershipRequest
): Promise<IBooleanResponse> => {
  try {
    const result = await transferOwnershipRequest({
      chatId: BigInt(data.groupId),
      newOwnerId: BigInt(data.newOwnerId),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.transferOwnership', error);
    throw buildModelError(error);
  }
};

export async function removeGroupParticipants(
  data: IGroupParticipantChangeRequest
): Promise<IBooleanResponse> {
  try {
    const result = await removeChatParticipantsRequest({
      chatId: BigInt(data.groupId),
      userIds: data.userIds.map((userId) => BigInt(userId)),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.removeGroupParticipants', error);
    throw buildModelError(error);
  }
}

export async function addGroupParticipants(
  data: IGroupParticipantChangeRequest
): Promise<IBooleanResponse> {
  try {
    const result = await addChatParticipantsRequest({
      chatId: BigInt(data.groupId),
      userIds: data.userIds.map((userId) => BigInt(userId)),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'userDoesNotExist':
        throw new WsRequestError(ServerErrorCode.UserDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.addGroupParticipants', error);
    throw buildModelError(error);
  }
}

export async function addGroupAdmins(
  data: IGroupParticipantChangeRequest
): Promise<IBooleanResponse> {
  try {
    const result = await addChatAdminsRequest({
      chatId: BigInt(data.groupId),
      userIds: data.userIds.map((userId) => BigInt(userId)),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'userDoesNotExist':
        throw new WsRequestError(ServerErrorCode.UserDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.addGroupAdmins', error);
    throw buildModelError(error);
  }
}

export async function removeGroupAdmins(
  data: IGroupParticipantChangeRequest
): Promise<IBooleanResponse> {
  try {
    const result = await revokeChatAdminsRequest({
      chatId: BigInt(data.groupId),
      userIds: data.userIds.map((userId) => BigInt(userId)),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.removeGroupAdmins', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function getKeywordFilters(_groupId: string): Promise<string[]> {
  try {
    return [];
  } catch (error) {
    logger.get('API').error('group.getKeywordFilters', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function setKeywordFilters(_data: ISetKeywordFiltersRequest): Promise<boolean> {
  try {
    return false;
  } catch (error) {
    logger.get('API').error('group.setKeywordFilters', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function getBannedUsersForGroup(
  _data: IGetBannedUsersForGroupRequest
): Promise<IGetBannedUsersForGroupResponse> {
  try {
    const result = { bannedUserIds: [] };
    return result;
  } catch (error) {
    logger.get('API').error('group.getBannedUsersForGroup', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function unbanUsersForGroup(_data: IUnbanUsersForGroupRequest): Promise<boolean> {
  try {
    return false;
  } catch (error) {
    logger.get('API').error('group.unbanUsersForGroup', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function getGroupAdvertisementSettings(
  _data: IGroupIdRequest
): Promise<IGroupAdvertisementSettingsResponse> {
  try {
    const result = { groupId: '', adsEnabled: false };

    return result;
  } catch (error) {
    logger.get('API').error('group.getGroupAdvertisementSettings', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function setGroupAdvertisementSettings(
  _data: ISetGroupAdvertisementSettingsRequest
): Promise<IGroupAdvertisementSettingsResponse> {
  try {
    const result = { groupId: '', adsEnabled: false };

    return result;
  } catch (error) {
    logger.get('API').error('group.setGroupAdvertisementSettings', error);
    throw buildModelError(error);
  }
}

export async function deleteGroupDeepLink(
  data: IDeleteGroupDeepLinkRequest
): Promise<IPersonalizedGroup> {
  try {
    const result = await refreshDeepLinkDetailsRequest({
      chatId: BigInt(data.groupId),
    });

    if (result.type.case === 'success') {
      return mapPersonalizedChatResponse(result.type.value);
    } else {
      throw result.type.value;
    }
  } catch (error) {
    logger.get('API').error('group.deleteGroupDeepLink', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function deepLinkExists(_data: IDeepLinkExistsRequest): Promise<boolean> {
  try {
    return false;
  } catch (error) {
    logger.get('API').error('group.deepLinkExists', error);
    throw buildModelError(error);
  }
}

export async function getGroupsByIds(
  groupIds: string[]
): Promise<ICollectionResponsePersonalizedGroup> {
  try {
    const result = await getChatsByIdsRequest({
      chatIds: groupIds.map((groupId) => BigInt(groupId)),
    });

    return {
      items: (result.type.value?.items || []).map((item) => mapPersonalizedChatResponse(item)),
      userBannedInGroups: [],
    };
  } catch (error) {
    logger.get('API').error('group.getGroupsByIds', error);
    throw buildModelError(error);
  }
}

export async function createGroupAndInvite(
  data: ICreateGroupRequest
): Promise<IGroupWithBlockStatusResponse> {
  try {
    const result = await createChatRequest({
      ...data,
      name: data.name ?? undefined,
      description: data.description ?? undefined,
      avatarUrl: data.avatarUrl ?? undefined,
      type: mapGroupTypeToChatType(data.type),
      participants: data.members ? data.members.map((id) => BigInt(id)) : [],
      admins: data.admins ? data.admins.map((id) => BigInt(id)) : [],
    });

    switch (result.type.case) {
      case 'invalidChatType':
        throw new WsRequestError(GroupServerErrorCode.GroupTypeIsInvalid, result);
      case 'wrongP2PChat':
        throw new WsRequestError(GroupServerErrorCode.P2PIsWrong, result);
      case 'userDoesNotExist':
        throw new WsRequestError(ServerErrorCode.UserDoesNotExist, result);
      case 'deepLinkTaken':
        throw new WsRequestError(GroupServerErrorCode.DeepLinkIsTaken, result);
      case 'deepLinkBadData':
        throw new WsRequestError(GroupServerErrorCode.DeepLinkBadData, result);
      case 'chatBadData':
        throw new WsRequestError(ServerErrorCode.BadData, result);
    }

    return {
      personalizedGroup: mapPersonalizedChatResponse(result.type.value!.personalizedChat!),
      groupCounter: mapGroupCounterView(result.type.value!.chatUpdate!),
      usersFailedToInvite: [],
    };
  } catch (error) {
    logger.get('API').error('group.createGroupAndInvite', error);
    throw buildModelError(error);
  }
}

// No need to BFF implement, this functionality has been closed by the feature flags
export async function getFriendsNotInvitedToGroup(
  _data: IGetFriendsNotInvitedToGroupRequest
): Promise<IGetFriendsNotInvitedToGroupResponse> {
  try {
    const result = { usersDisplayData: [] };

    return result;
  } catch (error) {
    logger.get('API').error('group.getFriendsNotInvitedToGroup', error);
    throw buildModelError(error);
  }
}

export async function pinGroup(data: IPinGroupRequest): Promise<boolean> {
  try {
    const result = await pinChatRequest({ chatId: BigInt(data.groupId) });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'pinnedChannelsLimit':
        throw new WsRequestError(GroupServerErrorCode.PinnedChannelsLimit, result);
      case 'pinnedGroupsLimit':
        throw new WsRequestError(GroupServerErrorCode.PinnedGroupsLimit, result);
      case 'unsupportedChatType':
        throw new WsRequestError(GroupServerErrorCode.GroupTypeIsUnsupported, result);
    }

    return result.type.case === 'success';
  } catch (error) {
    logger.get('API').error('group.pinGroup', error);
    throw buildModelError(error);
  }
}

export async function unpinGroup(data: IPinGroupRequest): Promise<IBooleanResponse> {
  try {
    const result = await unpinChatRequest({ chatId: BigInt(data.groupId) });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
    }

    return result.type.case === 'success' ? { result: true } : { result: false };
  } catch (error) {
    logger.get('API').error('group.unpinGroup', error);
    throw buildModelError(error);
  }
}

export async function updateGroupInfo(data: IUpdateGroupInfoRequest): Promise<IPersonalizedGroup> {
  try {
    const result = await updateChatRequest({
      ...data,
      chatId: BigInt(data.groupId),
      type: mapGroupTypeToChatType(data.groupType),
    });

    switch (result.type.case) {
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'invalidChatType':
        throw new WsRequestError(GroupServerErrorCode.GroupTypeIsInvalid, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
    }

    // ToDo Remove when the updateChatRequest will be return personalized group
    const groups = await getGroupsByIds([data.groupId]);
    if (!groups.items.length) {
      throw new WsRequestError(ServerErrorCode.EmptyData, result);
    }

    return groups.items[0];
  } catch (error) {
    logger.get('API').error('group.updateGroupInfo', error);
    throw buildModelError(error);
  }
}

// TODO: No need to BFF implement, this functionality has been closed by the feature flags
export async function getOrCreateThread(
  _data: IGetOrCreateThreadRequest
): Promise<IPersonalizedGroup> {
  try {
    const result = {} as IPersonalizedGroup;

    return result;
  } catch (error) {
    logger.get('API').error('group.getOrCreateThread', error);
    throw buildModelError(error);
  }
}

// TODO:No need to BFF implement, this functionality has been closed by the feature flags
export async function getThreadInfoById(
  _data: IGetThreadInfoByIdRequest
): Promise<IThreadInfoResponse> {
  try {
    const result = {} as IThreadInfoResponse;

    return result;
  } catch (error) {
    logger.get('API').error('group.getThreadInfoById', error);
    throw buildModelError(error);
  }
}

// TODO:No need to BFF implement, this functionality has been closed by the feature flags
export async function getThreadsInfo(_data: IGetThreadsInfoRequest): Promise<IThreadsInfoResponse> {
  try {
    const result = { items: [] };

    return result;
  } catch (error) {
    logger.get('API').error('group.getThreadsInfo', error);
    throw buildModelError(error);
  }
}

export async function clearGroupHistoryForMe(
  data: IClearGroupMessageRequest
): Promise<IBooleanResponse> {
  try {
    const result = await clearChatHistoryForMeRequest({ chatId: BigInt(data.groupId) });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('messaging.clearGroupHistory', error);
    throw buildModelError(error);
  }
}

export async function clearGroupHistoryForAll(
  data: IClearGroupMessageRequest
): Promise<IBooleanResponse> {
  try {
    const result = await clearChatHistoryForAllRequest({ chatId: BigInt(data.groupId) });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('messaging.clearGroupHistoryForAll', error);
    throw buildModelError(error);
  }
}

export async function turnNotificationInGroup(
  groupId: string,
  muted: boolean
): Promise<IBooleanResponse> {
  try {
    const result = await muteChatRequest({ chatId: BigInt(groupId), muted });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('messaging.turnNotificationInGroup', error);
    throw buildModelError(error);
  }
}

export async function leaveGroup(groupId: string): Promise<IBooleanResponse> {
  try {
    const result = await leaveChatRequest({ chatId: BigInt(groupId) });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('messaging.leaveGroup', error);
    throw buildModelError(error);
  }
}

export async function deleteGroup(groupId: string): Promise<IBooleanResponse> {
  try {
    const result = await removeChatRequest({ chatId: BigInt(groupId) });

    switch (result.type.case) {
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
      case 'invalidChatType':
        throw new WsRequestError(GroupServerErrorCode.GroupTypeIsInvalid, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('messaging.deleteGroup', error);
    throw buildModelError(error);
  }
}

export async function joinGroup(groupId: string): Promise<IPersonalizedGroup> {
  try {
    const result = await joinChatRequest({ chatId: BigInt(groupId) });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'chatDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.GroupDoesNotExist, result);
    }

    return mapPersonalizedChatResponse(result.type.value!);
  } catch (error) {
    logger.get('API').error('messaging.joinGroup', error);
    throw buildModelError(error);
  }
}

export const getUsersAddToGroup = async (
  data: IGetUsersAddToGroupRequest
): Promise<IGetUsersAddToGroupResponse> => {
  try {
    const result = await getUsersAddToChatRequest({
      chatId: data.groupId ? BigInt(data.groupId) : undefined,
      limit: data.limit,
      cursor: data.cursor,
    });

    if (result.type.case !== 'success') {
      return {
        users: [],
        hasMore: false,
      };
    }

    const searchResult = result.type.value;

    return {
      cursor: searchResult.cursor,
      users: searchResult.users.map((user) => mapPersonalizedUserResponse(user)),
      hasMore: !!searchResult.cursor,
    };
  } catch (error) {
    logger.get('API').error('search.searchUsersAddToGroup', error);
    throw buildModelError(error);
  }
};

export async function setUserTypingMessage(groupId: string): Promise<IBooleanResponse> {
  try {
    const result = await setUserTypingMessageRequest({ chatId: BigInt(groupId) });

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.userTypingMessage', error);
    throw buildModelError(error);
  }
}

export async function setChatMessagesHistoryTtl(
  data: ISetChatMessagesHistoryTtlRequest
): Promise<IBooleanResponse> {
  try {
    const result = await setChatMessagesHistoryTtlRequest(
      mapSetChatMessagesHistoryTtlRequest(data)
    );

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('group.setChatMessagesHistoryTtl', error);
    throw buildModelError(error);
  }
}
